import { useCallback, useContext, startTransition, useMemo } from 'react'
import { getFirAuth } from '../../Api/firebase/firebaseConnect'
import { authFns } from '../../Api/firebase/auth'
import { getFirebaseUser } from '../../Api/firebase/AuthData'
import {
  logBrazeEvent,
  withTimeout,
  AUTH_TIMEOUT,
  handleAuthError,
  clearQueryParams,
  logEvent,
} from '../../Utils'
import { ANALYTICS_TAGS } from '../../Constant'
import { LinkContext } from '../../Context'
import type { User } from '../../Api/Users/types'
import type { AuthOperations, AuthStateSetters } from './types'
import {
  fetchUserFromDB,
  setUserDataInState,
  handleAnonymousSignIn,
} from '../../Utils/auth/authOperations'
import { getUserId } from '../../Utils'

export const useAuthOperations = (
  toggleShowSnackbar: (snackbarData: {
    text: string
    color: 'red' | 'blue' | 'green'
  }) => void,
  authStateSetters: AuthStateSetters
): AuthOperations => {
  const { getMasterLinkParamsFromLocal } = useContext(LinkContext)!
  const { setUser, setUserFavorites, setAuthLoading, setShowLogin, resetAuthState } =
    authStateSetters

  const logout = useCallback(async (): Promise<void> => {
    try {
      const fba = await getFirAuth()
      if (!fba) {
        handleAuthError(
          ANALYTICS_TAGS.auth_change_error,
          'Firebase auth not available',
          'logout'
        )
        startTransition(() => {
          setAuthLoading(false)
        })
        return
      }

      startTransition(() => {
        resetAuthState()
      })
      await fba.signOut()
      await handleAnonymousSignIn()

      try {
        logBrazeEvent(ANALYTICS_TAGS.log_out)
        logEvent(ANALYTICS_TAGS.log_out)
      } catch (brazeError) {
        console.error('Error logging Braze logout event:', brazeError)
      }
    } catch (error) {
      handleAuthError(ANALYTICS_TAGS.auth_change_error, error, 'logout')
      startTransition(() => {
        setAuthLoading(false)
      })
    }
  }, [resetAuthState, setAuthLoading])

  const handleUserChange = useCallback(async (): Promise<void> => {
    try {
      const usr = await withTimeout(getFirebaseUser(), AUTH_TIMEOUT, 'getFirebaseUser')

      if (usr?.uid && !usr.isAnonymous) {
        const pllUser = await fetchUserFromDB(
          usr,
          getMasterLinkParamsFromLocal,
          toggleShowSnackbar
        )

        if (pllUser) {
          await setUserDataInState(pllUser, setUser, setUserFavorites, setAuthLoading)
        } else {
          handleAuthError(
            ANALYTICS_TAGS.user_fetch_failed,
            'Failed to fetch or create user in database',
            'handleUserChange',
            { userId: getUserId(usr) }
          )
          startTransition(() => {
            setAuthLoading(false)
            setShowLogin({ show: false, extraFields: [] })
          })
          await logout()
          toggleShowSnackbar({
            text: 'Failed to load user profile. Please try logging in again.',
            color: 'red',
          })
        }
      } else {
        await handleAnonymousSignIn()
        startTransition(() => {
          setUser(usr as User)
          setUserFavorites({
            topics: [],
            favPlayers: [],
            favTeam: null,
          })
          setShowLogin({ show: false, extraFields: [] })
          setAuthLoading(false)
        })
      }
    } catch (error) {
      handleAuthError(ANALYTICS_TAGS.user_change_error, error, 'handleUserChange')

      startTransition(() => {
        setAuthLoading(false)
        setShowLogin({ show: false, extraFields: [] })
      })
      toggleShowSnackbar({
        text: 'Error loading user data. Please try again.',
        color: 'red',
      })
    }
  }, [
    getMasterLinkParamsFromLocal,
    logout,
    setAuthLoading,
    setUser,
    setUserFavorites,
    setShowLogin,
    toggleShowSnackbar,
  ])

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    try {
      const auth = await getFirAuth()
      if (!auth) {
        handleAuthError(
          ANALYTICS_TAGS.auth_change_error,
          'Firebase auth not available',
          'getAuthToken'
        )
        return null
      }

      const usr = await getFirebaseUser()
      if (auth?.currentUser && !usr.isAnonymous && usr.uid) {
        return await auth.currentUser.getIdToken()
      }
      return null
    } catch (error) {
      handleAuthError(ANALYTICS_TAGS.auth_change_error, error, 'getAuthToken')
      return null
    }
  }, [])

  const handleCustomToken = useCallback(async (tk: string) => {
    const auth = await getFirAuth()
    if (!tk || !auth) return

    try {
      const { signInWithCustomToken } = await authFns
      const result = await signInWithCustomToken(auth, tk)
      if (result?.user?.uid) {
        clearQueryParams(['fbct'])
      }
    } catch (err) {
      handleAuthError(ANALYTICS_TAGS.auth_change_error, err, 'handleCustomToken')
      clearQueryParams(['fbct'])
    }
  }, [])

  const getAuthRedirectResult = useCallback(async () => {
    const auth = await getFirAuth()
    if (auth) {
      try {
        const { getRedirectResult, browserPopupRedirectResolver } = await authFns
        await getRedirectResult(auth, browserPopupRedirectResolver)
      } catch (err: any) {
        if (!err?.credential) return
      }
    }
  }, [])

  const waitForAuth = useCallback(async () => {
    const { waitForAuthStateReady } = await import(
      /* webpackChunkName: "firebase-connect" */ '../../Api/firebase/firebaseConnect'
    )
    await waitForAuthStateReady()
  }, [])

  return useMemo(
    () => ({
      handleUserChange,
      logout,
      getAuthToken,
      handleCustomToken,
      getAuthRedirectResult,
      waitForAuth,
    }),
    [
      handleUserChange,
      logout,
      getAuthToken,
      handleCustomToken,
      getAuthRedirectResult,
      waitForAuth,
    ]
  )
}
